<template>
  <v-card class="mt-5" outlined>
    <v-card-title class="d-flex align-center">
      <v-icon class="mr-2" color="primary">mdi-file-document-outline</v-icon>
      <span>Quotations Summary</span>
      <v-spacer />
      <v-btn icon @click="fetchSummaries" :loading="loading">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </v-card-title>
    <v-card-text>
      <!-- Filters -->
      <v-row class="mb-2" dense>
        <v-col cols="12" sm="4">
          <v-select
            v-model="filterBranchId"
            :items="branchFilterItems"
            label="Branch"
            dense
            outlined
            hide-details
            @change="fetchSummaries"
          />
        </v-col>
        <v-col cols="12" sm="4">
          <v-text-field
            v-model="search"
            prepend-icon="mdi-magnify"
            label="Search customer / company"
            dense
            outlined
            hide-details
            clearable
          />
        </v-col>
        <v-col cols="12" sm="4">
          <v-text-field
            v-model="searchNotes"
            prepend-icon="mdi-magnify"
            label="Search notes / quote #"
            dense
            outlined
            hide-details
            clearable
          />
        </v-col>
      </v-row>

      <v-data-table
        :items="filteredSummaries"
        :headers="headers"
        :loading="loading"
        item-key="id"
        dense
        no-data-text="No quotations found."
        loading-text="Loading quotations..."
      >
        <template v-slot:item.customer_name="{ item }">
          <div>
            <span class="font-weight-medium">{{ item.customer_name }}</span>
            <v-chip v-if="item.is_expired" color="red" dark x-small class="ml-2"
              >Expired</v-chip
            >
            <div v-if="item.company_name" class="caption grey--text">
              {{ item.company_name }}
            </div>
          </div>
        </template>
        <template v-slot:item.quote_number="{ item }">
          {{ item.quote_number || "—" }}
        </template>
        <template v-slot:item.branch="{ item }">
          {{ item.branch ? item.branch.branch_name : "Unassigned" }}
        </template>
        <template v-slot:item.items="{ item }">
          <v-chip small>{{ (item.quote_items || []).length }}</v-chip>
        </template>
        <template v-slot:item.inserted_by="{ item }">
          {{ item.inserted_by_hat || item.inserted_by_name || "—" }}
        </template>
        <template v-slot:item.created_at="{ item }">
          {{ formatDate(item.created_at) }}
        </template>
        <template v-slot:item.notes="{ item }">
          <v-tooltip bottom :disabled="!item.notes">
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">{{
                truncate(item.notes, 40) || "—"
              }}</span>
            </template>
            <span>{{ item.notes }}</span>
          </v-tooltip>
        </template>
        <template v-slot:item.total_quote_price="{ item }">
          &#8369;{{ formatCurrency(displayTotal(item)) }}
        </template>
        <template v-slot:item.action="{ item }">
          <div class="d-flex align-center">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  @click="viewQuotation(item)"
                >
                  <v-icon small>mdi-eye</v-icon>
                </v-btn>
              </template>
              <span>View items</span>
            </v-tooltip>
            <convert-to-invoice-launcher
              :quotation="item"
              @convert="onConvertQuote"
            />
          </div>
        </template>
      </v-data-table>

      <p class="caption grey--text text-right">quotationsLookup.vue</p>
    </v-card-text>

    <!-- View modal: items, quantity, supplier, shipping cost, quoted price -->
    <v-dialog v-model="viewDialog" max-width="1000px" scrollable>
      <v-card v-if="selected">
        <v-card-title class="d-flex align-center">
          <span>Quote #{{ selected.quote_number }}</span>
          <v-spacer />
          <convert-to-invoice-launcher
            :quotation="selected"
            @convert="onConvertQuote"
          />
          <v-btn icon @click="viewDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-subtitle>
          {{ selected.customer_name
          }}<span v-if="selected.company_name">
            — {{ selected.company_name }}</span
          >
          <span v-if="selected.quotation_date">
            &middot; {{ selected.quotation_date }}</span
          >
          <span v-if="selected.notes"> &middot; {{ selected.notes }}</span>
        </v-card-subtitle>
        <v-card-text>
          <v-simple-table dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th style="width: 40px">#</th>
                  <th>Item</th>
                  <th class="text-right">Qty</th>
                  <th>Unit</th>
                  <th>Supplier</th>
                  <th class="text-right">Shipping</th>
                  <th class="text-right">Unit Price</th>
                  <th class="text-right">Quoted Price</th>
                </tr>
              </thead>
              <tbody>
                <template
                  v-for="(group, gi) in numberedGroups(selected.quote_items)"
                >
                  <tr v-if="group.name" :key="'g' + gi" class="grey lighten-4">
                    <td colspan="8" class="font-weight-bold caption">
                      {{ group.name }}
                    </td>
                  </tr>
                  <tr v-for="qi in group.items" :key="qi.id">
                    <td class="grey--text">{{ qi.n }}</td>
                    <td>{{ qi.item_name }}</td>
                    <td class="text-right">{{ qi.quantity }}</td>
                    <td>{{ qi.unit }}</td>
                    <td>{{ qi.supplier || "—" }}</td>
                    <td class="text-right">
                      &#8369;{{ formatCurrency(qi.freight_in_per_unit) }}
                    </td>
                    <td class="text-right">
                      &#8369;{{ formatCurrency(qi.final_supplier_price) }}
                    </td>
                    <td class="text-right">
                      &#8369;{{ formatCurrency(qi.final_quote_price_total) }}
                    </td>
                  </tr>
                </template>
                <tr v-if="!(selected.quote_items || []).length">
                  <td colspan="8" class="text-center grey--text">No items.</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
          <div class="text-right mt-3">
            <div class="mb-1">
              <span class="grey--text text--darken-1">SOP:</span>
              <template v-if="Number(selected.sop_amount) > 0">
                <span class="font-weight-medium ml-1">
                  &#8369;{{ formatCurrency(selected.sop_amount) }}
                </span>
                <v-chip x-small color="deep-purple" dark class="ml-2">
                  {{
                    selected.sop_type === "figure"
                      ? "Fixed"
                      : formatCurrency(selected.sop_value) + "%"
                  }}
                </v-chip>
                <span
                  v-if="Number(selected.cheque_amount) > 0"
                  class="caption grey--text ml-2"
                >
                  Cheque: &#8369;{{ formatCurrency(selected.cheque_amount) }}
                </span>
              </template>
              <span v-else class="font-weight-medium ml-1">None</span>
            </div>
            <div class="font-weight-bold">
              Total quote amount: &#8369;{{
                formatCurrency(displayTotal(selected))
              }}
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- hidden instance used as the editor for quotation→invoice conversion -->
    <add-invoice-card ref="convertInvoiceCard" />
  </v-card>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import ConvertToInvoiceLauncher from "../Quotation/convertToInvoiceLauncher.vue";
import AddInvoiceCard from "../Invoice/addInvoiceCard.vue";
export default {
  components: {
    ConvertToInvoiceLauncher,
    AddInvoiceCard,
  },
  data() {
    return {
      filterBranchId: null,
      search: "",
      searchNotes: "",
      viewDialog: false,
      selected: null,
      headers: [
        { text: "Customer", value: "customer_name" },
        { text: "Quote #", value: "quote_number" },
        { text: "Branch", value: "branch", sortable: false },
        { text: "Items", value: "items", sortable: false },
        { text: "Inserted By", value: "inserted_by", sortable: false },
        { text: "Inserted Date", value: "created_at" },
        { text: "Notes", value: "notes", sortable: false },
        { text: "Total", value: "total_quote_price" },
        { text: "Actions", value: "action", sortable: false },
      ],
    };
  },
  computed: {
    ...mapGetters({
      summaries: "quotation_summary/summaries",
      loading: "quotation_summary/loading",
      branches: "branch/branches",
    }),
    branchItems() {
      return this.branches.map((b) => ({
        text: b.branch_name,
        value: b.id,
      }));
    },
    branchFilterItems() {
      return [{ text: "All Branches", value: null }]
        .concat(this.branchItems)
        .concat([{ text: "Unassigned", value: "none" }]);
    },
    filteredSummaries() {
      var customerQuery = (this.search || "").trim().toLowerCase();
      var notesQuery = (this.searchNotes || "").trim().toLowerCase();
      return this.summaries.filter((item) => {
        if (customerQuery) {
          var customerMatch =
            (item.customer_name || "").toLowerCase().includes(customerQuery) ||
            (item.company_name || "").toLowerCase().includes(customerQuery);
          if (!customerMatch) return false;
        }
        if (notesQuery) {
          var notesMatch =
            (item.notes || "").toLowerCase().includes(notesQuery) ||
            String(item.quote_number || "").includes(notesQuery);
          if (!notesMatch) return false;
        }
        return true;
      });
    },
  },
  methods: {
    ...mapActions({
      get_quotation_summaries: "quotation_summary/get_quotation_summaries",
      get_branches: "branch/get_branches",
    }),
    async fetchSummaries() {
      var params = {};
      if (this.filterBranchId !== null && this.filterBranchId !== undefined) {
        params.branch_id = this.filterBranchId;
      }
      try {
        await this.get_quotation_summaries(params);
      } catch (e) {
        this.$alert({
          title: "Error",
          message:
            "Error loading quotations: " +
            (e.response?.data?.message || e.message),
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
    },
    viewQuotation(item) {
      this.selected = item;
      this.viewDialog = true;
    },
    onConvertQuote(payload) {
      // hand the resolved customer + mapped rows to the hidden invoice card;
      // it runs the normal POST /invoice flow (stock/journal/SOP guards intact)
      this.viewDialog = false;
      this.$refs.convertInvoiceCard.openWithQuote(payload);
    },
    // Group quote items under their bundle headers, preserving server order.
    groupItems(quoteItems) {
      var groups = [];
      var byBundle = {};
      (quoteItems || []).forEach((qi) => {
        var key = qi.bundle_id != null ? "b" + qi.bundle_id : "none";
        if (!byBundle[key]) {
          byBundle[key] = {
            name: qi.bundle_id != null ? qi.bundle_name || "Bundle" : null,
            items: [],
          };
          groups.push(byBundle[key]);
        }
        byBundle[key].items.push(qi);
      });
      return groups;
    },
    // Same groups, with a running row number across bundle boundaries.
    // Copies each item so the Vuex state objects are never mutated.
    numberedGroups(quoteItems) {
      var n = 0;
      return this.groupItems(quoteItems).map((g) => ({
        name: g.name,
        items: g.items.map((qi) => Object.assign({}, qi, { n: ++n })),
      }));
    },
    displayTotal(item) {
      if (item.total_quote_price !== null && item.total_quote_price !== "")
        return item.total_quote_price;
      return item.total;
    },
    truncate(str, maxLen) {
      if (!str) return "";
      return str.length > maxLen ? str.substring(0, maxLen) + "..." : str;
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      var d = new Date(dateStr);
      if (isNaN(d)) return dateStr;
      return d.toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
      });
    },
    formatCurrency(value) {
      if (!value) return "0.00";
      return Number(value).toLocaleString("en-PH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
  },
  async created() {
    if (this.branches.length <= 0) {
      this.get_branches().catch(() => {});
    }
    await this.fetchSummaries();
  },
};
</script>
